/**
 * Table of Contents - Scroll Spy with smooth scrolling
 *
 * Used by: post_full.blade.php
 */

declare const bootstrap: {
    Collapse: {
        getInstance(element: Element | null): { hide(): void } | null;
    };
};

interface ScrollSpyConfig {
    readonly activationBreakpoint?: number;
    readonly scrollAnimationDuration?: number;
    readonly clickOverrideDuration?: number;
    readonly tocCollapseId?: string;
    readonly tocLinkSelector?: string;
}

const DEFAULT_CONFIG: Required<ScrollSpyConfig> = {
    activationBreakpoint: 960,
    scrollAnimationDuration: 400,
    clickOverrideDuration: 1000,
    tocCollapseId: 'toc-content',
    tocLinkSelector: '.table-of-contents-item'
};

function initTableOfContents(config: ScrollSpyConfig = {}): void {
    const settings = { ...DEFAULT_CONFIG, ...config };

    const tocCollapse = document.getElementById(settings.tocCollapseId);
    const tocLinks = document.querySelectorAll<HTMLAnchorElement>(settings.tocLinkSelector);

    if (tocLinks.length === 0) {
        return;
    }

    // Extract section IDs from the href attributes
    const sectionIds = Array.from(tocLinks)
        .map((link) => {
            return link.getAttribute('href');
        })
        .filter((id): id is string => {
            return id !== null && id.startsWith('#') && id.length > 1;
        });

    if (sectionIds.length === 0) {
        return;
    }

    const sections = document.querySelectorAll<HTMLElement>(sectionIds.join(','));

    let scrollSpyActive = false;
    let clickOverrideHref: string | null = null;
    let clickOverrideTimer: number | null = null;
    let scrollHandler: (() => void) | null = null;

    /**
     * Updates the active class based on which section is currently in the "sweet spot" of the viewport.
     */
    const onScroll = (): void => {
        const scrollOffset = window.innerHeight / 3;
        const currentScrollPos = window.scrollY;
        let scrollCalculatedHref: string | null = null;

        sections.forEach((section) => {
            if (section.offsetTop <= currentScrollPos + scrollOffset + 1) {
                scrollCalculatedHref = `#${section.id}`;
            }
        });

        const finalActiveHref = clickOverrideHref || scrollCalculatedHref;

        tocLinks.forEach((link) => {
            link.classList.toggle('active', link.getAttribute('href') === finalActiveHref);
        });
    };

    /**
     * Smooth scrolls to the target section when a TOC link is clicked.
     */
    const onLinkClick = (e: Event): void => {
        e.preventDefault();

        const link = e.currentTarget as HTMLAnchorElement;
        const targetHref = link.getAttribute('href');

        if (targetHref === null) {
            return;
        }

        const targetSection = document.querySelector<HTMLElement>(targetHref);

        if (targetSection === null) {
            return;
        }

        if (window.innerWidth > settings.activationBreakpoint) {
            window.smoothScrollTo(targetSection.offsetTop, settings.scrollAnimationDuration);

            clickOverrideHref = targetHref;

            if (clickOverrideTimer !== null) {
                clearTimeout(clickOverrideTimer);
            }

            clickOverrideTimer = window.setTimeout(() => {
                clickOverrideHref = null;
            }, settings.clickOverrideDuration);
        } else {
            const headerOffset = 100;
            const collapseHeight = tocCollapse?.offsetHeight || 0;
            const finalScrollPosition = targetSection.offsetTop - collapseHeight - headerOffset;

            bootstrap.Collapse.getInstance(tocCollapse)?.hide();
            window.smoothScrollTo(finalScrollPosition, settings.scrollAnimationDuration);
        }
    };

    /**
     * Handles mobile TOC menu closure.
     */
    tocCollapse?.addEventListener('hidden.bs.collapse', (e: Event) => {
        const target = e.currentTarget as HTMLElement;
        const targetHref = target.dataset.scrollTarget;

        if (targetHref) {
            const targetSection = document.querySelector<HTMLElement>(targetHref);

            if (targetSection) {
                window.smoothScrollTo(targetSection.offsetTop - 100, settings.scrollAnimationDuration);
            }

            delete target.dataset.scrollTarget;
        }
    });

    // Attach click handlers to TOC links
    tocLinks.forEach((link) => {
        link.addEventListener('click', onLinkClick);
    });

    const activateScrollSpy = (): void => {
        if (scrollSpyActive) {
            return;
        }

        scrollHandler = onScroll;
        window.addEventListener('scroll', scrollHandler);
        onScroll();
        scrollSpyActive = true;
    };

    const deactivateScrollSpy = (): void => {
        if (!scrollSpyActive) {
            return;
        }

        if (scrollHandler !== null) {
            window.removeEventListener('scroll', scrollHandler);
        }

        tocLinks.forEach((link) => {
            link.classList.remove('active');
        });

        scrollSpyActive = false;
    };

    const checkWidthAndToggle = (): void => {
        if (window.innerWidth > settings.activationBreakpoint) {
            activateScrollSpy();
        } else {
            deactivateScrollSpy();
        }
    };

    // Initialize state
    checkWidthAndToggle();
    window.addEventListener('resize', checkWidthAndToggle);
}

document.addEventListener('DOMContentLoaded', () => {
    if (document.querySelector('.table-of-contents-item')) {
        initTableOfContents();
    }
});

export { initTableOfContents };
